import type {
  AdminBusinessLineAssignmentsResponse,
  AdminLocalProcessorSettingsResponse,
  AdminOperationalReadinessResponse,
  AdminStaffGoogleWorkspaceBoundedSyncResponse,
  AdminStaffProfileSetupCompletenessResponse,
} from '@oracord/shared';

type Tone = 'emerald' | 'amber' | 'slate' | 'blue';

export type SetupSourceStatus = {
  state: 'ready' | 'needs_setup' | 'not_connected';
  label: string;
  detail: string;
  tone: Tone;
};

export function describeEngineHandoffStatus({
  openedSetupLink,
  engine,
}: {
  openedSetupLink: boolean;
  engine: AdminLocalProcessorSettingsResponse | null;
}): SetupSourceStatus {
  const summary = engine?.engine;
  const paired = engineCheckReady(summary, 'Paired');
  const businessSetupLoaded = engineCheckReady(summary, 'Business setup loaded');
  const running = engineCheckReady(summary, 'Running');

  if (paired && businessSetupLoaded) {
    return {
      state: 'ready',
      label: running ? 'Ready' : 'Paired',
      detail: running
        ? 'Engine is paired, company setup is loaded, and the background service is running.'
        : 'Engine is paired and company setup is loaded. Starting the background service is the next separate step.',
      tone: 'emerald',
    };
  }

  if (paired) {
    return {
      state: 'needs_setup',
      label: 'Paired, finishing setup',
      detail: 'Engine is paired. Waiting for the company setup package to load.',
      tone: 'blue',
    };
  }

  return {
    state: openedSetupLink ? 'needs_setup' : 'not_connected',
    label: openedSetupLink ? 'Waiting for Engine' : 'Not connected yet',
    detail: openedSetupLink
      ? 'Waiting for Oracord Engine to report setup progress. This will update automatically for a short time.'
      : 'Create a setup link and open it on the Mac with Oracord Engine installed.',
    tone: openedSetupLink ? 'amber' : 'slate',
  };
}

export function describeGoogleWorkspaceSetupStatus(
  staff: AdminStaffProfileSetupCompletenessResponse | null,
): SetupSourceStatus {
  if (!staff) {
    return {
      state: 'not_connected',
      label: 'Checking setup',
      detail: 'Loading Google Workspace setup state.',
      tone: 'slate',
    };
  }

  const summary = staff.summary;
  if (!staff.googleWorkspace.configured) {
    return {
      state: 'not_connected',
      label: 'Not connected yet',
      detail: 'Connect Google Workspace before staff can authorize Gmail and Calendar.',
      tone: 'slate',
    };
  }

  if (summary.activeProfiles === 0) {
    return {
      state: 'needs_setup',
      label: 'Needs team profiles',
      detail: 'Add staff profiles before Google Workspace authorization can be completed.',
      tone: 'amber',
    };
  }

  if (summary.needsWorkEmail > 0) {
    return {
      state: 'needs_setup',
      label: 'Needs staff work emails',
      detail: `${summary.needsWorkEmail} staff profile${plural(summary.needsWorkEmail)} need work email before Google authorization can be matched.`,
      tone: 'amber',
    };
  }

  if (summary.needsGoogleWorkspace > 0) {
    return {
      state: 'needs_setup',
      label: 'Needs staff authorization',
      detail: `${summary.needsGoogleWorkspace} staff profile${plural(summary.needsGoogleWorkspace)} still need Gmail and Calendar authorization.`,
      tone: 'amber',
    };
  }

  const readyCount = summary.readyForCurrentSetup;
  const syncedRows = staff.rows.filter(row => row.googleWorkspaceIngestion.ingestionActive || row.googleWorkspaceIngestion.lastSyncedAt).length;
  if (readyCount > 0 && syncedRows === 0) {
    return {
      state: 'needs_setup',
      label: 'Connected, not synced',
      detail: `${readyCount} staff profile${plural(readyCount)} have Google access connected. Run the first recent work-context sync to make Gmail and Calendar evidence available.`,
      tone: 'blue',
    };
  }

  return {
    state: readyCount > 0 ? 'ready' : 'needs_setup',
    label: readyCount > 0 ? 'Synced' : 'Needs setup',
    detail: readyCount > 0
      ? `${syncedRows} of ${readyCount} Google-connected staff profile${plural(readyCount)} have recorded Gmail or Calendar sync evidence.`
      : 'Google Workspace is configured, but no staff profiles are ready yet.',
    tone: readyCount > 0 ? 'emerald' : 'amber',
  };
}

export function googleWorkspaceSyncEligibleRows(
  staff: AdminStaffProfileSetupCompletenessResponse | null,
): AdminStaffProfileSetupCompletenessResponse['rows'] {
  if (!staff?.googleWorkspace.configured) return [];
  return staff.rows.filter(row => row.checks.googleWorkspace.status === 'ready' && (row.googleWorkspaceIngestion.gmail.livePollingAvailable || row.googleWorkspaceIngestion.calendar.livePollingAvailable));
}

export function describeGoogleWorkspaceSyncAction(staff: AdminStaffProfileSetupCompletenessResponse | null): SetupSourceStatus {
  const eligibleRows = googleWorkspaceSyncEligibleRows(staff);
  if (!staff) {
    return { state: 'not_connected', label: 'Checking setup', detail: 'Loading Google Workspace sync readiness.', tone: 'slate' };
  }
  if (!staff.googleWorkspace.configured) {
    return { state: 'not_connected', label: 'Connect Google first', detail: 'Connect Google Workspace before syncing recent Gmail and Calendar context.', tone: 'slate' };
  }
  if (eligibleRows.length === 0) {
    return { state: 'needs_setup', label: 'Needs authorization', detail: 'No staff profile has live Gmail or Calendar polling available yet.', tone: 'amber' };
  }
  const syncedRows = eligibleRows.filter(row => row.googleWorkspaceIngestion.ingestionActive || row.googleWorkspaceIngestion.lastSyncedAt).length;
  return {
    state: syncedRows > 0 ? 'ready' : 'needs_setup',
    label: syncedRows > 0 ? 'Sync available' : 'Ready to sync',
    detail:
      syncedRows > 0
        ? `${syncedRows} of ${eligibleRows.length} connected staff profile${plural(eligibleRows.length)} have prior sync evidence. You can refresh recent work context now.`
        : `${eligibleRows.length} connected staff profile${plural(eligibleRows.length)} can run a first bounded Gmail and Calendar sync now.`,
    tone: syncedRows > 0 ? 'emerald' : 'blue',
  };
}

export function summarizeGoogleWorkspaceSyncResults(results: AdminStaffGoogleWorkspaceBoundedSyncResponse[]): string {
  if (results.length === 0) return 'No Google Workspace sync was run.';
  const gmailItems = sum(results, result => result.gmail.itemCount);
  const gmailCreated = sum(results, result => result.gmail.created);
  const calendarItems = sum(results, result => result.calendar.itemCount);
  const calendarCreated = sum(results, result => result.calendar.created);
  const failures = results.flatMap(result => [result.gmail.sanitizedError, result.calendar.sanitizedError].filter((value): value is string => Boolean(value)));
  const latest = latestTimestamp(results.flatMap(result => [result.gmail.lastSyncedAt, result.calendar.lastSyncedAt]));
  const summary = `Gmail: ${gmailItems} item${plural(gmailItems)} checked, ${gmailCreated} created. Calendar: ${calendarItems} item${plural(calendarItems)} checked, ${calendarCreated} created.${latest ? ` Last synced ${formatTimestamp(latest)}.` : ''}`;
  if (failures.length === 0) return summary;
  return `${summary} ${failures.slice(0, 2).join(' ')}`;
}

export function describeBusinessLineSetupStatus({
  assignments,
  readiness,
}: {
  assignments: AdminBusinessLineAssignmentsResponse | null;
  readiness: AdminOperationalReadinessResponse | null;
}): SetupSourceStatus {
  if (!assignments || !readiness) {
    return {
      state: 'not_connected',
      label: 'Checking setup',
      detail: 'Loading business line setup state.',
      tone: 'slate',
    };
  }

  const assignedCount = assignments.rows.filter(row => row.assignmentStatus === 'assigned').length;
  const unassignedCount = assignments.rows.length - assignedCount;
  if (assignments.rows.length === 0) {
    return {
      state: 'not_connected',
      label: 'Not connected yet',
      detail: 'Add or buy a business line before calls can reach Oracord.',
      tone: 'slate',
    };
  }

  if (assignedCount === 0) {
    return {
      state: 'needs_setup',
      label: 'Needs assignment',
      detail: `${unassignedCount} business line${plural(unassignedCount)} need a staff profile assignment.`,
      tone: 'amber',
    };
  }

  if (readiness.businessLineSourceVerification.status === 'verified') {
    return {
      state: 'ready',
      label: 'Ready',
      detail: `${assignedCount} assigned business line${plural(assignedCount)} ${assignedCount === 1 ? 'has' : 'have'} same-org call or recording evidence.`,
      tone: 'emerald',
    };
  }

  const connectionStatus = readiness.businessLineConnection.status;
  if (connectionStatus === 'not_configured' || connectionStatus === 'needs_credentials' || connectionStatus === 'credentials_present_unverified') {
    return {
      state: 'needs_setup',
      label: 'Needs provider setup',
      detail: readiness.businessLineConnection.nextAction || 'Finish Twilio credentials and webhook setup before live calls can be verified.',
      tone: 'amber',
    };
  }

  return {
    state: 'needs_setup',
    label: 'Needs live traffic check',
    detail: readiness.businessLineSourceVerification.nextAction || 'Provider setup is present, but Oracord has not observed same-org call or recording evidence yet.',
    tone: 'blue',
  };
}

function engineCheckReady(engine: AdminLocalProcessorSettingsResponse['engine'] | null | undefined, label: string) {
  return engine?.checks.some(check => check.label === label && check.status === 'ready') ?? false;
}

function plural(count: number) {
  return count === 1 ? '' : 's';
}

function sum<T>(items: T[], selector: (item: T) => number) {
  return items.reduce((total, item) => total + selector(item), 0);
}

function latestTimestamp(values: Array<string | null | undefined>): string | null {
  const sorted = values.filter((value): value is string => Boolean(value)).sort((a, b) => Date.parse(b) - Date.parse(a));
  return sorted[0] ?? null;
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
